import React from 'react';
import {
    Box,
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    SxProps,
    Theme,
} from '@mui/material';
import { ReactComponent as ArrowDownIcon } from '../static/images/svg/iconamoon_arrow-up-2-fill.svg';
import { ReactComponent as CloseIcon } from '../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as DeleteOutlineIcon } from '../static/images/svg/ic_outline-delete.svg';

/** 下拉箭头：位于最右侧，与 PrefixLabeledPillSelect 一致 */
const PillSelectChevron = React.forwardRef<HTMLSpanElement>((_props, ref) => (
    <Box
        ref={ref}
        component="span"
        sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--icon-primary-300)',
            pointerEvents: 'none',
        }}
    >
        <ArrowDownIcon style={{ width: 24, height: 24 }} />
    </Box>
));
PillSelectChevron.displayName = 'PillSelectChevron';

/** 输入框内右侧留白：垃圾桶图标区 + 下拉箭头区（px） */
// const RIGHT_PADDING = 40 + 36;

/** 选项结构：与 PrefixLabeledPillSelect 一致 */
export interface PrefixLabeledPillMultiSelectOption {
    /** 选项唯一值 */
    value: string;
    /** 展示名称 */
    label: string;
}

export interface PrefixLabeledPillMultiSelectProps {
    /** 选项列表 */
    options: PrefixLabeledPillMultiSelectOption[];
    /** 当前选中的 value 数组 */
    value: string[];
    /** 选中变更回调 */
    onChange: (nextValues: string[]) => void;
    /** 展示在名称前的附属标签（如「门店-」），可选 */
    labelPrefix?: string;
    /** 左侧图标区域，可选 */
    leadingIcon?: React.ReactNode;
    /** 未选任何项时的占位文案 */
    placeholder?: string;
    /** 占位文案字号（覆盖 size 默认 14/20） */
    placeholderFontSize?: number | string;
    /** 大小，默认 medium */
    size?: 'small' | 'medium';
    /** 最大宽度，避免多选时过宽 */
    maxWidth?: number;
    /** 下拉内「全取消」按钮文案，可由调用方传 i18n.t('xxx')，默认「全取消」 */
    clearAllLabel?: string;
    sx?: SxProps<Theme>;
}

/**
 * 多选圆角药丸下拉：选中项以 Chip 形式展示在框内，点击 Chip 的删除图标可取消选中。
 * 样式与 PrefixLabeledPillSelect 保持一致（白底圆角、可选 leadingIcon/前缀、下拉箭头）。
 */
const PrefixLabeledPillMultiSelect: React.FC<PrefixLabeledPillMultiSelectProps> = ({
    options,
    value,
    onChange,
    labelPrefix = '',
    leadingIcon,
    placeholder = '',
    placeholderFontSize,
    size = 'medium',
    maxWidth = 480,
    sx,
}) => {
    /** 统一规范为 string[]，避免调用方传入 number[] 或 Option[] 导致 includes 匹配不到 */
    const valueStr = (value ?? []).map((v) => (typeof v === 'object' && v != null && 'value' in v ? String((v as { value: unknown }).value) : String(v)));


    const handleChange = (e: SelectChangeEvent<string[]>) => {
        const next = e.target.value;
        onChange(typeof next === 'string' ? next.split(',') : [...next]);
    };

    /** 在框内点击某个已选 Chip 的删除图标时，仅移除该项，不打开下拉 */
    const handleRemove = (removedValue: string) => (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onChange(valueStr.filter((v) => v !== removedValue));
    };

    /** 点击输入框右侧垃圾桶图标：清空全部选中，不打开下拉 */
    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onChange([]);
    };

    const selectedOptions = options.filter((o) => valueStr.includes(String(o.value)));
    const pillRadius = size === 'small' ? '8px' : '16px';
    const minHeight = size === 'small' ? 32 : 48;

    return (
        <FormControl variant="standard" sx={{ m: 0, minWidth: 200, maxWidth: maxWidth ?? 480 }}>
            <Select
                multiple
                value={valueStr}
                onChange={handleChange}
                disableUnderline
                IconComponent={PillSelectChevron}
                displayEmpty
                renderValue={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            py: 0.5,
                            minHeight: size === 'small' ? 24 : 32,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {leadingIcon != null ? (
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    flexShrink: 0,
                                    width: 32,
                                    height: 32,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--icon-primary-300)',
                                }}
                            >
                                {leadingIcon}
                            </Box>
                        ) : null}
                        {selectedOptions.length === 0 ? (
                            <Box
                                component="span"
                                sx={{
                                    fontSize:
                                        placeholderFontSize != null
                                            ? placeholderFontSize
                                            : size === 'small'
                                                ? 14
                                                : 20,
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    color: 'var(--text-tertiary)',
                                }}
                            >
                                {placeholder}
                            </Box>
                        ) : (
                            selectedOptions.map((opt) => (
                                <Chip
                                    key={opt.value}
                                    size="small"
                                    label={labelPrefix + opt.label}
                                    onDelete={handleRemove(opt.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    deleteIcon={
                                        <Box
                                            component="span"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            sx={{ display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <CloseIcon style={{ width: 16, height: 16 }} />
                                        </Box>
                                    }
                                    sx={{
                                        height: size === 'small' ? 24 : 28,
                                        fontSize: size === 'small' ? 12 : 14,
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        '& .MuiChip-deleteIcon': {
                                            color: 'var(--icon-primary-300)',
                                            '&:hover': {
                                                color: 'var(--icon-primary-500)',
                                            },
                                        },
                                    }}
                                    variant="outlined"
                                />
                            ))
                        )}
                        {selectedOptions.length > 0 ? (
                            <IconButton
                                size="small"
                                onClick={handleClearAll}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    flexShrink: 0,
                                    width: 32,
                                    height: 32,
                                    color: 'var(--text-dark)',
                                    bgcolor: 'var(--fill-neutral-200)',
                                    '&:hover': { color: 'var(--text-dark)', bgcolor: 'var(--fill-neutral-200)' },
                                    '& svg, & path': { fill: 'currentColor' },
                                }}
                                aria-label="clear all"
                            >
                                <DeleteOutlineIcon style={{ width: 20, height: 20 }} />
                            </IconButton>
                        ) : null}
                    </Box>
                )}
                sx={{
                    borderRadius: pillRadius,
                    bgcolor: 'var(--fill-neutral-100)',
                    boxShadow: '0px 0px 2px var(--shadow-50)',
                    px: 2,
                    minHeight,
                    maxWidth: maxWidth ?? 480,
                    position: 'relative',
                    '&.MuiInputBase-root.Mui-focused': {
                        bgcolor: 'var(--fill-neutral-100)',
                    },
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                        pr: '40px !important',
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: pillRadius,
                        '&:focus, &:focus-visible': {
                            bgcolor: 'var(--fill-neutral-100)',
                        },
                    },
                    '&::before, &::after': { display: 'none' },
                    ...(sx || {}),
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: 2,
                            mt: 0.5,
                            boxShadow: '0px 4px 12px var(--shadow-25)',
                            bgcolor: 'var(--fill-neutral-100)',
                            maxHeight: 320,
                        },
                    },
                }}
            >
                {options.map((opt) => (
                    <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{ fontSize: 16, color: 'var(--text-dark)' }}
                    >
                        {labelPrefix}
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default PrefixLabeledPillMultiSelect;
